<template>
  <div>
    <input
      ref="fileInputRef"
      type="file"
      multiple
      accept=".txt,.csv,.md,.json,.xml,.yaml,.yml,.jpg,.jpeg,.png,.gif,.webp,.pdf,text/plain,text/csv,text/markdown,application/json,application/xml,text/yaml,image/jpeg,image/png,image/gif,image/webp,application/pdf"
      class="hidden"
      @change="handleFileInputChange"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import Dropzone from 'dropzone';
import { getCookie } from '../utils/cookie.js';

const VITE_APP_API_URL = import.meta.env.VITE_APP_API_URL;

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  maxSizeMb: {
    type: Number,
    default: 32
  }
});

const emit = defineEmits(['files-updated', 'error', 'update:modelValue']);

const uploadedFiles = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});
const fileInputRef = ref(null);
let dropzone = null;

const validateFile = (file) => {
  const maxSize = props.maxSizeMb * 1024 * 1024;
  const allowedTypes = [
    'text/plain', 'text/csv', 'text/markdown', 'application/json',
    'application/xml', 'text/yaml', 'image/jpeg', 'image/png',
    'image/gif', 'image/webp', 'application/pdf'
  ];
  const allowedExtensions = [
    '.txt', '.csv', '.md', '.json', '.xml', '.yaml', '.yml',
    '.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf'
  ];

  if (file.size > maxSize) {
    emit('error', `File ${file.name} is too large. Maximum size is ${props.maxSizeMb}MB.`);
    return false;
  }

  const isValidType = allowedTypes.includes(file.type) ||
    allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

  if (!isValidType) {
    emit('error', `File ${file.name} is not supported. Allowed formats: TXT, CSV, MD, JSON, XML, YAML, JPG, PNG, GIF, WEBP, PDF.`);
    return false;
  }

  return true;
};

const handleFileSelect = () => {
  fileInputRef.value?.click();
};

const handleFileInputChange = (event) => {
  const files = Array.from(event.target.files || []);
  files.forEach(file => {
    if (validateFile(file)) {
      dropzone.addFile(file);
    }
  });
  event.target.value = '';
};

const handleRemoveFile = (fileId) => {
  const index = uploadedFiles.value.findIndex(f => f.id === fileId);
  if (index > -1) {
    const file = uploadedFiles.value[index];
    if (file.dropzoneFile) {
      dropzone.removeFile(file.dropzoneFile);
    }
    uploadedFiles.value.splice(index, 1);
    emitFilesUpdate();
  }
};

const handleFilesFromOverlay = (files) => {
  files.forEach(file => {
    if (validateFile(file)) {
      dropzone.addFile(file);
    }
  });
};

const emitFilesUpdate = () => {
  const fileIds = uploadedFiles.value
    .filter(f => f.status === 'success' && f.serverId)
    .map(f => f.serverId);

  emit('files-updated', {
    files: uploadedFiles.value,
    fileIds: fileIds
  });
};

const clearFiles = () => {
  uploadedFiles.value = [];
};

defineExpose({
  handleFileSelect,
  clearFiles,
  getFileIds: () => uploadedFiles.value
    .filter(f => f.status === 'success' && f.serverId)
    .map(f => f.serverId),
  handleFilesFromOverlay,
  handleRemoveFile
});

onMounted(() => {
  const hiddenDropzoneElement = document.createElement('div');
  hiddenDropzoneElement.style.display = 'none';
  document.body.appendChild(hiddenDropzoneElement);

  const authToken = getCookie('token');

  dropzone = new Dropzone(hiddenDropzoneElement, {
    url: `${VITE_APP_API_URL}/api/v2/files/upload`,
    maxFiles: 5,
    maxFilesize: props.maxSizeMb,
    acceptedFiles: '.txt,.csv,.md,.json,.xml,.yaml,.yml,.jpg,.jpeg,.png,.gif,.webp,.pdf,text/plain,text/csv,text/markdown,application/json,application/xml,text/yaml,image/jpeg,image/png,image/gif,image/webp,application/pdf',
    addRemoveLinks: false,
    clickable: false,
    autoProcessQueue: true,
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    init() {
      this.on('addedfile', (file) => {
        if (!validateFile(file)) {
          this.removeFile(file);
          return;
        }

        if (uploadedFiles.value.length >= 5) {
          emit('error', 'Maximum 5 files allowed per message.');
          this.removeFile(file);
          return;
        }

        const fileId = Date.now() + Math.random();
        const fileObj = {
          id: fileId,
          name: file.name,
          size: file.size,
          status: 'uploading',
          dropzoneFile: file
        };

        uploadedFiles.value.push(fileObj);
        emitFilesUpdate();
      });

      this.on('success', (file, response) => {
        const fileObj = uploadedFiles.value.find(f => f.dropzoneFile === file);
        if (fileObj) {
          fileObj.status = 'success';
          fileObj.serverId = response.data.id;
          emitFilesUpdate();
        }
      });

      this.on('error', (file, errorMessage) => {
        const fileObj = uploadedFiles.value.find(f => f.dropzoneFile === file);
        if (fileObj && file.status !== 'canceled') {
          fileObj.status = 'error';

          let errorMsg = `Failed to upload ${file.name}`;

          if (errorMessage && typeof errorMessage === 'object') {
            if (errorMessage.message) {
              errorMsg = errorMessage.message;
            } else if (errorMessage.errors && errorMessage.errors.file) {
              errorMsg = Array.isArray(errorMessage.errors.file)
                ? errorMessage.errors.file[0]
                : errorMessage.errors.file;
            }
          } else if (typeof errorMessage === 'string') {
            errorMsg = errorMessage;
          }

          emit('error', errorMsg);
          emitFilesUpdate();
        }
      });

      this.on('removedfile', (file) => {
        const index = uploadedFiles.value.findIndex(f => f.dropzoneFile === file);
        if (index > -1) {
          uploadedFiles.value.splice(index, 1);
          emitFilesUpdate();
        }
      });
    }
  });
});

onUnmounted(() => {
  if (dropzone) {
    dropzone.destroy();
  }
});
</script>
